// src/pages/components/RiskProfileQuestionnaireForm.jsx
import "./css/questionnareform.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { request } from "../../services/server";

/**
 * RiskProfileQuestionnaireForm (workspace/orgnr scoped)
 *
 * Uses the unified quizzes endpoint so you can load/save
 * risk + ddq + owner via one backend surface.
 *
 * Uses:
 *  GET  /valuation/workspace/<orgnr>/quizzes/
 *  POST /valuation/workspace/<orgnr>/quizzes/  body: { risk: {...} }
 *
 * IMPORTANT:
 * - We no longer ask the user to pick "customer concentration" as a score.
 * - Instead we ask for the TOP 5 customers (EX moms), grouped/aggregated.
 * - The app calculates customer concentration and writes the score into `customer_concentration`.
 *
 * NOTE:
 * The backend risk model currently stores customer_concentration as an int 0–5.
 * This UI computes that score and sends it as before, so the backend does not need changes.
 */

function defaultQuizzesUrl(orgnr) {
  return `/valuation/workspace/${encodeURIComponent(orgnr)}/quizzes/`;
}

function parseAmount(v) {
  // accepts: "1200000", "1 200 000", "1,200,000"
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[^\d]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function formatSek(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(n));
}

function clampScore(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, Math.round(n)));
}

/**
 * Score mapping (same idea as previous dropdown):
 * - score 1: largest <10% and top3 <30%
 * - score 2: largest 10–20% and top3 <50%
 * - score 3: largest 20–30% OR top3 50–60%
 * - score 4: largest 30–50% OR top3 60–75%
 * - score 5: largest >50% OR top3 >75%
 *
 * Input:
 * - top customers amounts (ex moms) for last 12 months
 * - optional "other revenue" to approximate total (ex moms)
 */
function computeCustomerConcentrationScore({ topCustomers, otherRevenue }) {
  const amounts = (topCustomers || [])
    .map((c) => parseAmount(c.amount_ex_vat))
    .filter((n) => n != null && n > 0)
    .sort((a, b) => b - a);

  const other = parseAmount(otherRevenue) || 0;
  const topSum = amounts.reduce((a, b) => a + b, 0);

  const total = topSum + other;

  // If we cannot estimate total, we can't compute a meaningful share -> score 0 (unknown)
  if (!total || total <= 0 || amounts.length === 0) {
    return {
      score: 0,
      total,
      top1Share: null,
      top3Share: null,
      top5Share: null,
      amounts,
    };
  }

  const top1 = amounts[0] || 0;
  const top3 = (amounts[0] || 0) + (amounts[1] || 0) + (amounts[2] || 0);
  const top5 = amounts.slice(0, 5).reduce((a, b) => a + b, 0);

  const top1Share = top1 / total;
  const top3Share = top3 / total;
  const top5Share = top5 / total;

  let score = 1;

  if (top1Share > 0.5 || top3Share > 0.75) score = 5;
  else if (top1Share >= 0.3 || top3Share >= 0.6) score = 4;
  else if (top1Share >= 0.2 || top3Share >= 0.5) score = 3;
  else if (top1Share >= 0.1 || top3Share >= 0.3) score = 2;
  else score = 1;

  return {
    score,
    total,
    top1Share,
    top3Share,
    top5Share,
    amounts,
  };
}

function pct(x) {
  if (x == null || !Number.isFinite(x)) return "—";
  return `${Math.round(x * 100)}%`;
}

export default function RiskProfileQuestionnaireForm({ orgnr, onSaved, quizzesUrl }) {
  const effectiveQuizzesUrl = quizzesUrl || defaultQuizzesUrl(orgnr);
  const [values, setValues] = useState({
    key_person_risk: 0,
    // customer_concentration will be computed; keep it in state for saving
    customer_concentration: 0,
    supplier_dependency: 0,
    contract_quality: 0,
    financial_health: 0,
    compliance_risk: 0,

    // NEW: input for computing customer concentration (not sent to backend directly)
    top_customers: [
      { group: "", amount_ex_vat: "" },
      { group: "", amount_ex_vat: "" },
      { group: "", amount_ex_vat: "" },
      { group: "", amount_ex_vat: "" },
      { group: "", amount_ex_vat: "" },
    ],
    other_revenue_ex_vat: "", // remainder revenue (ex moms) last 12 months
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const abortRef = useRef(null);

  // --------------------------------------------------------
  // Load existing risk profile when orgnr changes
  // --------------------------------------------------------
  useEffect(() => {
    if (!orgnr) {
      setLoading(false);
      return;
    }

    try {
      abortRef.current?.abort();
    } catch {}
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let aborted = false;
    setLoading(true);
    setError("");

    request(effectiveQuizzesUrl, { signal: ctrl.signal })
      .then((bundle) => {
        if (aborted) return;
        const data = bundle?.risk || null;

        if (!data) {
          setSavedAt(null);
          return;
        }

        // Hydrate top_customers från backend om sparat (5 rader format
        // {group, amount_ex_vat}); annars behåll tom default. Backend
        // sparar {name, amount} — normalisera till frontend-form.
        const savedTop = Array.isArray(data.top_customers) ? data.top_customers : [];
        const hydratedTop = savedTop.length
          ? savedTop.slice(0, 5).map((c) => ({
              group: c?.name || c?.group || "",
              amount_ex_vat:
                c?.amount != null ? String(c.amount) :
                c?.amount_ex_vat != null ? String(c.amount_ex_vat) : "",
            }))
          : null;

        setValues((prev) => ({
          ...prev,
          key_person_risk: data.key_person_risk ?? 0,
          customer_concentration: data.customer_concentration ?? 0,
          supplier_dependency: data.supplier_dependency ?? 0,
          contract_quality: data.contract_quality ?? 0,
          financial_health: data.financial_health ?? 0,
          compliance_risk: data.compliance_risk ?? 0,
          // Hydrera bara om vi har sparad data; behåll tom default annars
          // så placeholder-fälten renderas korrekt.
          ...(hydratedTop ? { top_customers: [
            ...hydratedTop,
            ...Array(Math.max(0, 5 - hydratedTop.length)).fill({ group: "", amount_ex_vat: "" }),
          ] } : {}),
          ...(data.other_revenue_ex_vat != null
            ? { other_revenue_ex_vat: String(data.other_revenue_ex_vat) }
            : {}),
        }));

        setSavedAt(data.updated_at || null);
      })
      .catch((err) => {
        if (aborted) return;
        console.error("Failed to load risk profile", err);
        setError(
          err?.body?.detail ||
            "Kunde inte hämta riskprofil just nu. Du kan ändå göra en bedömning nedan."
        );
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });

    return () => {
      aborted = true;
      try {
        ctrl.abort();
      } catch {}
    };
  }, [orgnr]);

  // --------------------------------------------------------
  // Customer concentration: compute continuously from inputs
  // --------------------------------------------------------
  const concentration = useMemo(() => {
    return computeCustomerConcentrationScore({
      topCustomers: values.top_customers,
      otherRevenue: values.other_revenue_ex_vat,
    });
  }, [values.top_customers, values.other_revenue_ex_vat]);

  // Keep the score synced into values.customer_concentration (so saving works).
  // Only overwrite when the user has actually entered at least one customer
  // amount. Otherwise the score sits at 0 ("vet ej") and would clobber the
  // value the broker loaded from the saved bundle.
  useEffect(() => {
    if (!(concentration.amounts && concentration.amounts.length > 0)) return;
    setValues((prev) => {
      const nextScore = clampScore(concentration.score);
      if (prev.customer_concentration === nextScore) return prev;
      return { ...prev, customer_concentration: nextScore };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concentration.score, concentration.amounts.length]);

  // --------------------------------------------------------
  // Helpers
  // --------------------------------------------------------
  const handleChange = (field) => (e) => {
    setValues((prev) => ({
      ...prev,
      [field]: Number(e.target.value),
    }));
  };

  const handleTopCustomerChange = (idx, key) => (e) => {
    const value = e.target.value;
    setValues((prev) => {
      const next = [...(prev.top_customers || [])];
      next[idx] = { ...(next[idx] || {}), [key]: value };
      return { ...prev, top_customers: next };
    });
  };

  const handleOtherRevenue = (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, other_revenue_ex_vat: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgnr) return;

    setSaving(true);
    setError("");

    try {
      // Persistera även rådata för kundkoncentration så broker inte
      // tappar topp-5-listan vid sidladdning. Backend lagrar dem som
      // JSONField + DecimalField; vi normaliserar belopp till siffror.
      const topCustomersPayload = (values.top_customers || [])
        .map((c) => ({
          name: (c?.group || "").trim(),
          amount: parseAmount(c?.amount_ex_vat),
        }))
        .filter((c) => c.name || c.amount != null);

      const otherRevenue = parseAmount(values.other_revenue_ex_vat);

      const payload = {
        key_person_risk: values.key_person_risk ?? 0,
        customer_concentration: values.customer_concentration ?? 0, // computed
        supplier_dependency: values.supplier_dependency ?? 0,
        contract_quality: values.contract_quality ?? 0,
        financial_health: values.financial_health ?? 0,
        compliance_risk: values.compliance_risk ?? 0,
        top_customers: topCustomersPayload,
        other_revenue_ex_vat: otherRevenue,
      };

      const bundle = await request(effectiveQuizzesUrl, {
        method: "POST",
        body: { risk: payload },
      });

      const saved = bundle?.risk || null;
      const updated = saved?.updated_at || new Date().toISOString();

      setSavedAt(updated);
      if (onSaved && saved) onSaved(saved);
    } catch (err) {
      console.error("Failed to save risk profile", err);
      setError(err?.body?.detail || "Kunde inte spara riskprofilen. Försök igen senare.");
    } finally {
      setSaving(false);
    }
  };

  const renderScaleHint = () => (
    <p className="questionnaire-scale-hint">
      Välj det alternativ som bäst stämmer. <strong>0 = vet ej / inte bedömt.</strong>
    </p>
  );

  if (loading) return <p>Laddar riskprofil…</p>;

  return (
    <form onSubmit={handleSubmit} className="riskprofile-form">
      {error && <p className="questionnaire-error">{error}</p>}

      {savedAt && (
        <p className="questionnaire-meta">
          Senast uppdaterad: {new Date(savedAt).toLocaleString("sv-SE")}
        </p>
      )}

      <div className="questionnaire-intro">
        <p>
          Nedan skattar du några centrala riskfaktorer i bolaget. Det behöver inte vara perfekt – vi
          vill få en rimlig bild av nuläget inför värdering och due diligence.
        </p>
        {renderScaleHint()}
      </div>

      {/* 1. Nyckelpersoner */}
      <fieldset className="risk-card">
        <legend>Nyckelpersoner</legend>

        <p className="risk-question">Hur beroende är bolaget av enstaka nyckelpersoner i vardagen?</p>
        <p className="risk-help">
          Tänk på ägare, VD eller andra personer där kundrelationer eller kritisk kompetens framför allt
          sitter hos individen.
        </p>

        <select value={values.key_person_risk} onChange={handleChange("key_person_risk")}>
          <option value={0}>0 – Vet ej / inte bedömt</option>
          <option value={1}>Bolaget fungerar även om en nyckelperson slutar. Roller och kunskap är spridda.</option>
          <option value={2}>Det finns viktiga personer, men deras roller kan ersättas inom några månader.</option>
          <option value={3}>1–2 personer är tydligt viktiga, men det finns viss redundans.</option>
          <option value={4}>1–2 personer är avgörande och svåra att ersätta på kort sikt.</option>
          <option value={5}>Verksamheten är i praktiken beroende av en person (ofta ägaren).</option>
        </select>
      </fieldset>

      {/* 2. Kundbas – TOP 5 (computed concentration) */}
      <fieldset className="risk-card">
        <legend>Kunder (Top 5)</legend>

        <p className="risk-question">Vilka är era 5 största kunder – och hur mycket står de för?</p>
        <p className="risk-help">
          Skriv in <strong>kundgrupp/koncern</strong> och <strong>omsättning ex moms</strong> (senaste 12 månader).
          <br />
          <strong>Viktigt:</strong> Om flera bolag hör ihop (t.ex. samma koncern), skriv dem som <strong>en</strong>{" "}
          grupp så vi får en realistisk koncentration.
        </p>

        <div className="dashboard-two-col dashboard-two-col--stack-mobile">
          <div>
            <div className="risk-help" style={{ marginBottom: 8 }}>
              Top 5 kunder (grupperade)
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {values.top_customers.map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 0.8fr",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <input
                    type="text"
                    placeholder={`Kundgrupp ${idx + 1} (t.ex. “Koncern X”)`}
                    value={row.group}
                    onChange={handleTopCustomerChange(idx, "group")}
                  />
                  <input
                    type="text"
                    placeholder="Belopp ex moms (SEK)"
                    value={row.amount_ex_vat}
                    onChange={handleTopCustomerChange(idx, "amount_ex_vat")}
                  />
                </div>
              ))}
            </div>

            <label style={{ marginTop: 12, display: "block" }}>
              Övrig omsättning ex moms (resten utanför top 5, senaste 12 mån)
              <input
                type="text"
                placeholder="Exempel: 2 400 000"
                value={values.other_revenue_ex_vat}
                onChange={handleOtherRevenue}
              />
            </label>
          </div>

          <div>
            <div className="risk-help" style={{ marginBottom: 8 }}>
              Beräknad kundkoncentration
            </div>

            <div
              style={{
                border: "1px solid rgba(148,163,184,0.35)",
                background: "rgba(248,250,252,0.8)",
                borderRadius: 12,
                padding: 12,
              }}
            >
              <div style={{ fontSize: 12, color: "#475569" }}>Total (approx, ex moms)</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
                {formatSek(concentration.total)} SEK
              </div>

              <div style={{ display: "grid", gap: 6, marginTop: 10, fontSize: 12, color: "#0f172a" }}>
                <div>Största kund: <strong>{pct(concentration.top1Share)}</strong></div>
                <div>Top 3: <strong>{pct(concentration.top3Share)}</strong></div>
                <div>Top 5: <strong>{pct(concentration.top5Share)}</strong></div>
              </div>

              <div style={{ marginTop: 10, fontSize: 12, color: "#475569" }}>
                Risk-score (0–5) som sparas:
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2 }}>
                {values.customer_concentration}
              </div>

              {values.customer_concentration === 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: "#64748b" }}>
                  Tips: fyll i minst 1 kund + “övrig omsättning” så kan vi räkna andelar.
                </div>
              )}
            </div>
          </div>
        </div>
      </fieldset>

      {/* 3. Leverantörs- och plattformsberoende */}
      <fieldset className="risk-card">
        <legend>Leverantörs- och plattformsberoende</legend>

        <p className="risk-question">Hur beroende är bolaget av enstaka leverantörer, system eller plattformar?</p>
        <p className="risk-help">
          Tänk på kritiska materialleverantörer, logistikpartners, mjukvaruplattformar och andra system som
          verksamheten inte klarar sig utan.
        </p>

        <select value={values.supplier_dependency} onChange={handleChange("supplier_dependency")}>
          <option value={0}>0 – Vet ej / inte bedömt</option>
          <option value={1}>Flera likvärdiga alternativ; enkelt att byta.</option>
          <option value={2}>Några är viktiga, men realistiska alternativ finns.</option>
          <option value={3}>Ett fåtal är viktiga; byte kräver viss insats.</option>
          <option value={4}>1–2 är centrala; svårt att ersätta på kort sikt.</option>
          <option value={5}>Starkt beroende; avbrott skulle stoppa verksamheten.</option>
        </select>
      </fieldset>

      {/* 4. Avtalsbas och intäktsmodell */}
      <fieldset className="risk-card">
        <legend>Avtalsbas och intäktsmodell</legend>

        <p className="risk-question">Hur ser intäktsmodellen ut idag?</p>
        <p className="risk-help">
          Titta på hur stor del som är återkommande (abonnemang, ramavtal) jämfört med projekt och engångsaffärer.
        </p>

        <select value={values.contract_quality} onChange={handleChange("contract_quality")}>
          <option value={0}>0 – Vet ej / inte bedömt</option>
          <option value={1}>Hög andel återkommande intäkter (&gt; 70 %) med 12+ mån bindning/uppsägningstid.</option>
          <option value={2}>Blandning; återkommande ca 40–70 % och kunder stannar normalt flera år.</option>
          <option value={3}>Blandning av projekt och återkommande; mer variation mellan år.</option>
          <option value={4}>Mestadels projekt/engångsaffärer; få längre avtal.</option>
          <option value={5}>Nästan enbart kortsiktiga uppdrag utan återkommande avtal.</option>
        </select>
      </fieldset>

      {/* 5. Compliance, tillstånd och regelverk */}
      <fieldset className="risk-card">
        <legend>Compliance, tillstånd och regelverk</legend>

        <p className="risk-question">Hur ser situationen ut kring tillstånd, regler och compliance?</p>
        <p className="risk-help">
          Tänk på GDPR, arbetsmiljö, miljökrav och eventuella tillstånd eller branschspecifika regelverk.
        </p>

        <select value={values.compliance_risk} onChange={handleChange("compliance_risk")}>
          <option value={0}>0 – Vet ej / inte bedömt</option>
          <option value={1}>I huvudsak oreglerad; vanliga krav hanteras löpande utan kända problem.</option>
          <option value={2}>Vissa tillstånd/regelverk; ansvar tydligt och arbetet strukturerat.</option>
          <option value={3}>Krav finns, men mer ad hoc; inga kända större problem.</option>
          <option value={4}>Kända brister som borde åtgärdas men inte är gjort än.</option>
          <option value={5}>Kända brister och/eller pågående ärenden med myndigheter.</option>
        </select>
      </fieldset>

      <button type="submit" disabled={saving || !orgnr} className="questionnaire-submit">
        {saving ? "Sparar…" : "Spara riskprofil"}
      </button>

      {!orgnr && (
        <p className="questionnaire-meta">
          (Riskprofilen sparas först när organisationsnummer är kopplat till bolaget.)
        </p>
      )}
    </form>
  );
}
